import { useState, useEffect, useMemo, useRef } from "react";
import {
  X, Printer, Download, Barcode, Tag, Sparkles, Copy, Check,
  Search, FileText, Layers, Settings2, Sliders, CheckSquare, Square,
  Package, Boxes, IndianRupee, Eye, RefreshCw, ZoomIn, ZoomOut
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import BarcodeRenderer from "./BarcodeRenderer";
import { subscribeMasterSkus } from "../lib/dataStore";

// Standard retail label preset dimensions
export const LABEL_PRESETS = [
  {
    id: "thermal_50x38",
    name: "Thermal 50 × 38 mm (2\" × 1.5\")",
    shortName: "50×38mm Thermal",
    type: "roll",
    widthMm: 50,
    heightMm: 38,
    widthPx: 190,
    heightPx: 144,
    description: "Standard retail barcode sticker with MRP & specifications",
    defaultFontSize: "text-[10px]",
  },
  {
    id: "thermal_50x25",
    name: "Thermal 50 × 25 mm (2\" × 1\")",
    shortName: "50×25mm Compact",
    type: "roll",
    widthMm: 50,
    heightMm: 25,
    widthPx: 190,
    heightPx: 95,
    description: "Compact barcode sticker for price tag & SKU",
    defaultFontSize: "text-[9px]",
  },
  {
    id: "thermal_75x50",
    name: "Thermal 75 × 50 mm (3\" × 2\")",
    shortName: "75×50mm Detailed",
    type: "roll",
    widthMm: 75,
    heightMm: 50,
    widthPx: 284,
    heightPx: 190,
    description: "Large detailed hangtag sticker with complete compliance info",
    defaultFontSize: "text-[11px]",
  },
  {
    id: "a4_24",
    name: "A4 Sheet (24 Labels - 3 × 8)",
    shortName: "A4 24-up Sheet",
    type: "sheet",
    cols: 3,
    rows: 8,
    perSheet: 24,
    widthMm: 64,
    heightMm: 33.9,
    widthPx: 242,
    heightPx: 128,
    description: "Standard desktop laser/inkjet sticker sheets",
    defaultFontSize: "text-[9px]",
  },
  {
    id: "a4_30",
    name: "A4 Sheet (30 Labels - 3 × 10 Avery 5160)",
    shortName: "A4 30-up Avery",
    type: "sheet",
    cols: 3,
    rows: 10,
    perSheet: 30,
    widthMm: 66.7,
    heightMm: 25.4,
    widthPx: 252,
    heightPx: 96,
    description: "High-density 1\" × 2.6\" address/price label sheets",
    defaultFontSize: "text-[8px]",
  },
];

export default function ProductLabelModal({
  isOpen = true,
  onClose,
  initialItem = null, // Can be Master SKU or Catalog Product
  masterSkus = [],
  products = [],
}) {
  // Mode: "single" or "batch"
  const [mode, setMode] = useState("single");

  // Selected preset
  const [selectedPresetId, setSelectedPresetId] = useState("thermal_50x38");
  const preset = useMemo(() => {
    return LABEL_PRESETS.find((p) => p.id === selectedPresetId) || LABEL_PRESETS[0];
  }, [selectedPresetId]);

  // Current item being configured in single mode
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSourceType, setSelectedSourceType] = useState("master"); // "master" | "catalog" | "custom"

  // Label Form Data
  const [labelData, setLabelData] = useState({
    brandName: "Lineacraft India",
    productName: "",
    sku: "",
    barcode: "",
    barcodeFormat: "CODE128", // "CODE128" | "EAN13" | "CODE39"
    mrp: "",
    salePrice: "",
    showSalePrice: false,
    category: "",
    material: "",
    colour: "",
    size: "",
    weight: "",
    netQuantity: "1 Unit",
    mfgMonthYear: new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" }).format(new Date()),
    countryOfOrigin: "Made in India",
    customerCare: "care@lineacraft.in",
    footerText: "Crafted in Santiniketan",
  });

  // Display toggles
  const [options, setOptions] = useState({
    showBrand: true,
    showProductName: true,
    showSku: true,
    showBarcode: true,
    showBarcodeText: true,
    showMrp: true,
    showOfferPrice: false,
    showNetQty: true,
    showMfgDate: true,
    showOrigin: true,
    showMaterial: true,
    showSize: true,
    showColour: true,
    showBorder: true,
    showCareContact: false,
  });

  // Print copies
  const [printCopies, setPrintCopies] = useState(1);
  const [zoomScale, setZoomScale] = useState(1.2);
  const [isExporting, setIsExporting] = useState(false);

  // Batch Print Selection: array of { id, sku, name, mrp, salePrice, stock, copies, selected: boolean }
  const [batchList, setBatchList] = useState([]);
  const [batchSearch, setBatchSearch] = useState("");

  const labelPreviewRef = useRef(null);

  const [activeMasterSkus, setActiveMasterSkus] = useState(masterSkus || []);

  useEffect(() => {
    if (masterSkus && masterSkus.length > 0) {
      setActiveMasterSkus(masterSkus);
      return;
    }
    const unsub = subscribeMasterSkus((list) => {
      if (list && list.length > 0) setActiveMasterSkus(list);
    });
    return () => unsub && unsub();
  }, [masterSkus]);

  // Prepopulate labelData when initialItem changes or on first open
  useEffect(() => {
    if (!initialItem) {
      if (activeMasterSkus && activeMasterSkus.length > 0) {
        loadItemIntoLabel(activeMasterSkus[0], "master");
      }
      return;
    }

    // Determine if initialItem is a Master SKU or Catalog Product
    if (initialItem.sku && initialItem.purchaseCost !== undefined) {
      loadItemIntoLabel(initialItem, "master");
    } else {
      loadItemIntoLabel(initialItem, "catalog");
    }
  }, [initialItem, activeMasterSkus]);

  // Initialize Batch List from Master SKUs
  useEffect(() => {
    if (!activeMasterSkus || activeMasterSkus.length === 0) return;
    setBatchList(
      activeMasterSkus.map((s) => ({
        id: s.id,
        sku: s.sku,
        name: s.name,
        category: s.category || "",
        barcode: s.barcode || s.sku,
        mrp: s.mrp ? String(s.mrp) : s.saleCost ? String(s.saleCost) : "",
        salePrice: s.saleCost ? String(s.saleCost) : "",
        stock: Number(s.quantity || 0),
        material: s.material || "",
        colour: s.colour || "",
        size: s.size || "",
        copies: Number(s.quantity || 0) > 0 ? Number(s.quantity) : 1,
        selected: initialItem?.sku === s.sku,
      }))
    );
  }, [activeMasterSkus, initialItem]);

  // Helper to populate fields
  const loadItemIntoLabel = (item, type = "master") => {
    if (!item) return;

    if (type === "master") {
      const mrpVal = item.mrp ? String(item.mrp) : item.saleCost ? String(item.saleCost) : "";
      const saleVal = item.saleCost ? String(item.saleCost) : "";
      const hasOffer = Boolean(mrpVal && saleVal && Number(mrpVal) > Number(saleVal));

      setLabelData((prev) => ({
        ...prev,
        productName: item.name || "Handcrafted Product",
        sku: item.sku || "",
        barcode: item.barcode || item.sku || "",
        mrp: mrpVal,
        salePrice: saleVal,
        showSalePrice: hasOffer,
        category: item.category || "",
        material: item.material || "",
        colour: item.colour || "",
        size: item.size || "",
        weight: item.weight || "",
        netQuantity: "1 Unit",
        countryOfOrigin: "Made in India",
      }));

      setOptions((prev) => ({
        ...prev,
        showOfferPrice: hasOffer,
      }));

      if (Number(item.quantity) > 0) {
        setPrintCopies(Math.min(Number(item.quantity), 50));
      }
    } else {
      // Catalog Product
      const mrpVal = item.mrp ? String(item.mrp) : item.price ? String(item.price) : "";
      const saleVal = item.price ? String(item.price) : "";
      const hasOffer = Boolean(mrpVal && saleVal && Number(mrpVal) > Number(saleVal));
      const firstVariant = item.variants?.[0] || {};
      const skuVal = item.sku || firstVariant.sku || `LC-${item.id?.slice(0, 6) || "BAG"}`;

      setLabelData((prev) => ({
        ...prev,
        productName: item.name || "Artisanal Bag",
        sku: skuVal,
        barcode: item.barcode || skuVal,
        mrp: mrpVal,
        salePrice: saleVal,
        showSalePrice: hasOffer,
        category: item.category || "",
        material: item.material || "",
        colour: firstVariant.tag || item.colour || "",
        size: item.size || "",
        weight: item.weight || "",
        netQuantity: "1 Unit",
        countryOfOrigin: "Made in India",
      }));

      setOptions((prev) => ({
        ...prev,
        showOfferPrice: hasOffer,
      }));

      if (Number(item.stock) > 0) {
        setPrintCopies(Math.min(Number(item.stock), 50));
      }
    }
  };

  // Filtered lists for switcher dropdown
  const filteredMasterSkus = useMemo(() => {
    if (!searchQuery.trim()) return activeMasterSkus.slice(0, 15);
    const q = searchQuery.toLowerCase();
    return activeMasterSkus.filter(
      (s) =>
        s.sku.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        (s.category && s.category.toLowerCase().includes(q))
    ).slice(0, 25);
  }, [activeMasterSkus, searchQuery]);

  const filteredCatalogProducts = useMemo(() => {
    if (!searchQuery.trim()) return products.slice(0, 15);
    const q = searchQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.category && p.category.toLowerCase().includes(q))
    ).slice(0, 25);
  }, [products, searchQuery]);

  // Batch search filter
  const filteredBatchList = useMemo(() => {
    if (!batchSearch.trim()) return batchList;
    const q = batchSearch.toLowerCase();
    return batchList.filter(
      (b) =>
        b.sku.toLowerCase().includes(q) ||
        b.name.toLowerCase().includes(q) ||
        b.category.toLowerCase().includes(q)
    );
  }, [batchList, batchSearch]);

  const totalBatchCopies = useMemo(() => {
    return batchList.filter((b) => b.selected).reduce((acc, b) => acc + (Number(b.copies) || 1), 0);
  }, [batchList]);

  // Handle direct print
  const handlePrint = () => {
    window.print();
  };

  // Handle high-resolution PDF download using jsPDF
  const handleDownloadPdf = async () => {
    setIsExporting(true);
    toast.loading("Generating print-ready PDF...", { id: "label-pdf" });

    try {
      if (mode === "single") {
        const isRoll = preset.type === "roll";
        // Create jsPDF with exact label dimensions
        const pdf = new jsPDF({
          orientation: preset.widthMm > preset.heightMm ? "landscape" : "portrait",
          unit: "mm",
          format: isRoll ? [preset.widthMm, preset.heightMm] : "a4",
        });

        const targetEl = document.getElementById("single-label-render-target");
        if (!targetEl) throw new Error("Label element not found");

        const canvas = await html2canvas(targetEl, {
          scale: 4, // 4x for ultra sharp 300+ DPI vector-like clarity
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");

        if (isRoll) {
          for (let i = 0; i < printCopies; i++) {
            if (i > 0) pdf.addPage([preset.widthMm, preset.heightMm], preset.widthMm > preset.heightMm ? "landscape" : "portrait");
            pdf.addImage(imgData, "PNG", 0, 0, preset.widthMm, preset.heightMm);
          }
        } else {
          // A4 Sheet Layout: tile onto sheet
          const marginX = 8;
          const marginY = 10;
          const colWidth = (210 - marginX * 2) / (preset.cols || 3);
          const rowHeight = (297 - marginY * 2) / (preset.rows || 8);

          let currentLabel = 0;
          let pageStarted = false;

          while (currentLabel < printCopies) {
            if (currentLabel > 0 && currentLabel % preset.perSheet === 0) {
              pdf.addPage("a4", "portrait");
            }
            const posInSheet = currentLabel % preset.perSheet;
            const c = posInSheet % preset.cols;
            const r = Math.floor(posInSheet / preset.cols);

            const x = marginX + c * colWidth + 1;
            const y = marginY + r * rowHeight + 1;
            const w = colWidth - 2;
            const h = rowHeight - 2;

            pdf.addImage(imgData, "PNG", x, y, w, h);
            currentLabel++;
          }
        }

        const safeSku = (labelData.sku || "LABEL").replace(/[^a-zA-Z0-9_-]/g, "_");
        pdf.save(`${safeSku}-barcode-labels-${preset.id}.pdf`);
        toast.success("PDF downloaded successfully!", { id: "label-pdf" });
      } else {
        // Batch PDF mode
        const selectedItems = batchList.filter((b) => b.selected);
        if (selectedItems.length === 0) {
          toast.error("Please select at least one SKU to print in batch", { id: "label-pdf" });
          setIsExporting(false);
          return;
        }

        const pdf = new jsPDF({
          orientation: preset.widthMm > preset.heightMm ? "landscape" : "portrait",
          unit: "mm",
          format: preset.type === "roll" ? [preset.widthMm, preset.heightMm] : "a4",
        });

        // We can render each batch item sequentially
        const container = document.getElementById("batch-render-container");
        if (!container) throw new Error("Batch render container not found");

        const cards = container.querySelectorAll(".batch-label-card");
        let pageCount = 0;

        for (let i = 0; i < cards.length; i++) {
          const card = cards[i];
          const copies = Number(card.getAttribute("data-copies") || 1);

          const canvas = await html2canvas(card, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
          });
          const imgData = canvas.toDataURL("image/png");

          for (let c = 0; c < copies; c++) {
            if (pageCount > 0) {
              pdf.addPage([preset.widthMm, preset.heightMm], preset.widthMm > preset.heightMm ? "landscape" : "portrait");
            }
            pdf.addImage(imgData, "PNG", 0, 0, preset.widthMm, preset.heightMm);
            pageCount++;
          }
        }

        pdf.save(`batch-sku-labels-${selectedItems.length}-skus.pdf`);
        toast.success(`Downloaded batch labels (${pageCount} total labels)!`, { id: "label-pdf" });
      }
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. You can also use 'Print Now'.", { id: "label-pdf" });
    } finally {
      setIsExporting(false);
    }
  };

  // Download single label as PNG image (useful for thermal label software like Dymo, Zebra Designer, Bartender)
  const handleDownloadPng = async () => {
    setIsExporting(true);
    toast.loading("Generating high-res PNG...", { id: "label-png" });
    try {
      const targetEl = document.getElementById("single-label-render-target");
      if (!targetEl) throw new Error("Label not found");

      const canvas = await html2canvas(targetEl, {
        scale: 4,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${labelData.sku || "product"}-label-${preset.widthMm}x${preset.heightMm}mm.png`;
      a.click();
      toast.success("Label image saved as PNG!", { id: "label-png" });
    } catch (err) {
      console.error("PNG error:", err);
      toast.error("Failed to export image", { id: "label-png" });
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      data-testid="product-label-modal"
    >
      <div className="bg-card text-card-foreground border border-border w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center">
              <Barcode className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-serif text-xl font-bold text-foreground">
                  Product Label &amp; Barcode Generator
                </h2>
                <span className="text-[11px] font-medium bg-terracotta/10 text-terracotta px-2 py-0.5 rounded-full border border-terracotta/20">
                  MRP &amp; Barcode Ready
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Generate high-density retail barcode stickers, MRP tags &amp; thermal labels for Lineacraft items
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Mode switch tabs */}
            <div className="inline-flex items-center bg-muted p-0.5 rounded-lg border border-border text-xs mr-2">
              <button
                type="button"
                data-testid="label-mode-single"
                onClick={() => setMode("single")}
                className={`px-3 py-1.5 rounded-md font-medium transition ${
                  mode === "single"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Single Item
              </button>
              <button
                type="button"
                data-testid="label-mode-batch"
                onClick={() => setMode("batch")}
                className={`px-3 py-1.5 rounded-md font-medium transition flex items-center gap-1.5 ${
                  mode === "batch"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>Batch Print</span>
                {totalBatchCopies > 0 && (
                  <span className="bg-terracotta text-white text-[10px] px-1.5 py-0.2 rounded-full font-mono">
                    {totalBatchCopies}
                  </span>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
              data-testid="close-label-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {mode === "single" ? (
            <>
              {/* Left Column: Form & Settings (5 cols) */}
              <div className="lg:col-span-6 space-y-5">
                {/* 1. Quick Select / Source Switcher */}
                <div className="bg-muted/40 p-3.5 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-terracotta" />
                      Select Product / SKU
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setSelectedSourceType("master")}
                        className={`text-[11px] px-2 py-0.5 rounded ${
                          selectedSourceType === "master"
                            ? "bg-terracotta text-white font-medium"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        Master SKUs ({activeMasterSkus.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSourceType("catalog")}
                        className={`text-[11px] px-2 py-0.5 rounded ${
                          selectedSourceType === "catalog"
                            ? "bg-terracotta text-white font-medium"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        Catalog ({products.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSourceType("custom")}
                        className={`text-[11px] px-2 py-0.5 rounded ${
                          selectedSourceType === "custom"
                            ? "bg-terracotta text-white font-medium"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        Custom Entry
                      </button>
                    </div>
                  </div>

                  {selectedSourceType !== "custom" && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder={
                            selectedSourceType === "master"
                              ? "Search Master SKU by code, name..."
                              : "Search Store product..."
                          }
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full text-xs pl-8 pr-3 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta"
                        />
                      </div>

                      <div className="max-h-36 overflow-y-auto border border-border rounded bg-background divide-y divide-border text-xs">
                        {selectedSourceType === "master" ? (
                          filteredMasterSkus.length > 0 ? (
                            filteredMasterSkus.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => loadItemIntoLabel(s, "master")}
                                className={`w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between transition ${
                                  labelData.sku === s.sku ? "bg-terracotta/10 text-terracotta font-medium" : ""
                                }`}
                              >
                                <div className="truncate pr-2">
                                  <span className="font-mono font-bold mr-1.5 text-foreground">{s.sku}</span>
                                  <span className="text-muted-foreground truncate">{s.name}</span>
                                </div>
                                <div className="text-right whitespace-nowrap">
                                  <span className="font-price font-semibold text-foreground">
                                    ₹{s.mrp || s.saleCost || "0"}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground ml-1.5">
                                    ({s.quantity || 0} in stock)
                                  </span>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="p-3 text-center text-muted-foreground">No matching SKUs found</div>
                          )
                        ) : filteredCatalogProducts.length > 0 ? (
                          filteredCatalogProducts.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => loadItemIntoLabel(p, "catalog")}
                              className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between transition"
                            >
                              <span className="truncate pr-2">{p.name}</span>
                              <span className="font-price font-semibold whitespace-nowrap">₹{p.mrp || p.price}</span>
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-center text-muted-foreground">No matching products found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. Primary Label Content: MRP, Barcode, SKU, Title */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Label Information
                    </span>
                    <span className="text-[11px] text-muted-foreground">Appears directly on printed tag</span>
                  </div>

                  {/* Product Title & Brand */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        Brand / Store Header
                      </label>
                      <input
                        type="text"
                        data-testid="label-input-brand"
                        placeholder="Lineacraft India"
                        value={labelData.brandName}
                        onChange={(e) => setLabelData({ ...labelData, brandName: e.target.value })}
                        className="w-full text-xs px-2.5 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        Product Title
                      </label>
                      <input
                        type="text"
                        data-testid="label-input-name"
                        value={labelData.productName}
                        onChange={(e) => setLabelData({ ...labelData, productName: e.target.value })}
                        className="w-full text-xs px-2.5 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta font-medium"
                      />
                    </div>
                  </div>

                  {/* SKU & Barcode */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        Custom SKU Code
                      </label>
                      <input
                        type="text"
                        data-testid="label-input-sku"
                        value={labelData.sku}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setLabelData({
                            ...labelData,
                            sku: val,
                            barcode: labelData.barcode === labelData.sku ? val : labelData.barcode,
                          });
                        }}
                        className="w-full text-xs px-2.5 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta font-mono font-bold uppercase"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        Barcode Value
                      </label>
                      <input
                        type="text"
                        data-testid="label-input-barcode"
                        value={labelData.barcode}
                        onChange={(e) => setLabelData({ ...labelData, barcode: e.target.value })}
                        className="w-full text-xs px-2.5 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-foreground mb-1">
                        Barcode Type
                      </label>
                      <select
                        value={labelData.barcodeFormat}
                        onChange={(e) => setLabelData({ ...labelData, barcodeFormat: e.target.value })}
                        className="w-full text-xs px-2.5 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta font-mono"
                      >
                        <option value="CODE128">Code 128 (Universal)</option>
                        <option value="EAN13">EAN-13 (13 Digits)</option>
                        <option value="CODE39">Code 39</option>
                      </select>
                    </div>
                  </div>

                  {/* MRP & Sale Price */}
                  <div className="p-3 bg-terracotta/5 border border-terracotta/20 rounded-lg space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-foreground mb-1 flex items-center justify-between">
                          <span>MRP (₹)</span>
                          <span className="text-[10px] text-muted-foreground font-normal">Incl. of all taxes</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-foreground">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="any"
                            data-testid="label-input-mrp"
                            value={labelData.mrp}
                            onChange={(e) => setLabelData({ ...labelData, mrp: e.target.value })}
                            placeholder="0.00"
                            className="w-full text-sm font-bold font-price pl-7 pr-3 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-foreground">
                            Offer / Sale Price (₹)
                          </label>
                          <label className="text-[11px] text-muted-foreground cursor-pointer flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={options.showOfferPrice}
                              onChange={(e) => setOptions({ ...options, showOfferPrice: e.target.checked })}
                              className="rounded text-terracotta focus:ring-terracotta"
                            />
                            <span>Show on label</span>
                          </label>
                        </div>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="any"
                            value={labelData.salePrice}
                            onChange={(e) => setLabelData({ ...labelData, salePrice: e.target.value })}
                            placeholder="Optional sale rate"
                            className="w-full text-sm font-price pl-7 pr-3 py-1.5 rounded bg-background border border-border outline-none focus:border-terracotta"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Specifications & Compliance */}
                  <div className="border border-border rounded-lg p-3 space-y-2 bg-card">
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider block">
                      Retail Compliance &amp; Specs
                    </span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Material</label>
                        <input
                          type="text"
                          value={labelData.material}
                          onChange={(e) => setLabelData({ ...labelData, material: e.target.value })}
                          placeholder="e.g. Leather / Cotton"
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Colour / Tag</label>
                        <input
                          type="text"
                          value={labelData.colour}
                          onChange={(e) => setLabelData({ ...labelData, colour: e.target.value })}
                          placeholder="e.g. Tan / Black"
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Size / Dimensions</label>
                        <input
                          type="text"
                          value={labelData.size}
                          onChange={(e) => setLabelData({ ...labelData, size: e.target.value })}
                          placeholder="e.g. 38 × 28 cm"
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Net Quantity</label>
                        <input
                          type="text"
                          value={labelData.netQuantity}
                          onChange={(e) => setLabelData({ ...labelData, netQuantity: e.target.value })}
                          placeholder="1 Unit / 1 N"
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Month &amp; Year of Pkg</label>
                        <input
                          type="text"
                          value={labelData.mfgMonthYear}
                          onChange={(e) => setLabelData({ ...labelData, mfgMonthYear: e.target.value })}
                          placeholder="Sep 2026"
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Country of Origin</label>
                        <input
                          type="text"
                          value={labelData.countryOfOrigin}
                          onChange={(e) => setLabelData({ ...labelData, countryOfOrigin: e.target.value })}
                          className="w-full text-[11px] px-2 py-1 rounded bg-background border border-border"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Field Display Toggles */}
                <div className="bg-muted/30 p-3 rounded-lg border border-border">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
                    Visible Fields on Sticker
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showBrand}
                        onChange={(e) => setOptions({ ...options, showBrand: e.target.checked })}
                      />
                      <span>Brand Header</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showMrp}
                        onChange={(e) => setOptions({ ...options, showMrp: e.target.checked })}
                      />
                      <span className="font-semibold text-terracotta">MRP Tag</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showBarcode}
                        onChange={(e) => setOptions({ ...options, showBarcode: e.target.checked })}
                      />
                      <span className="font-semibold">Barcode Bars</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showSku}
                        onChange={(e) => setOptions({ ...options, showSku: e.target.checked })}
                      />
                      <span>SKU Code</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showNetQty}
                        onChange={(e) => setOptions({ ...options, showNetQty: e.target.checked })}
                      />
                      <span>Net Qty</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showMfgDate}
                        onChange={(e) => setOptions({ ...options, showMfgDate: e.target.checked })}
                      />
                      <span>Pkg Date</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showMaterial}
                        onChange={(e) => setOptions({ ...options, showMaterial: e.target.checked })}
                      />
                      <span>Material</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.showBorder}
                        onChange={(e) => setOptions({ ...options, showBorder: e.target.checked })}
                      />
                      <span>Sticker Border</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column: Live Preview & Print Settings (6 cols) */}
              <div className="lg:col-span-6 flex flex-col justify-between space-y-4">
                <div className="space-y-4">
                  {/* Preset Selector */}
                  <div className="bg-muted/40 p-3.5 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <Settings2 className="w-3.5 h-3.5" />
                        Label Dimension &amp; Printer Paper
                      </span>
                      <span className="text-[11px] text-terracotta font-medium">
                        {preset.widthMm} × {preset.heightMm} mm
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {LABEL_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`preset-${p.id}`}
                          onClick={() => setSelectedPresetId(p.id)}
                          className={`text-left p-2 rounded-lg border transition text-xs ${
                            selectedPresetId === p.id
                              ? "bg-background border-terracotta shadow-sm text-foreground font-medium"
                              : "bg-background/60 border-border text-muted-foreground hover:text-foreground hover:border-terracotta/40"
                          }`}
                        >
                          <div className="font-semibold text-foreground flex items-center justify-between">
                            <span>{p.shortName}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">
                              {p.type === "roll" ? "Roll" : "Sheet"}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{p.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Live Preview Panel */}
                  <div className="bg-muted/20 border border-border rounded-xl p-4 flex flex-col items-center">
                    <div className="w-full flex items-center justify-between text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1 font-medium text-foreground">
                        <Eye className="w-3.5 h-3.5 text-terracotta" />
                        Real-Time Label Preview
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setZoomScale((z) => Math.max(0.8, Number((z - 0.2).toFixed(1))))}
                          className="p-1 rounded hover:bg-muted"
                          title="Zoom Out"
                        >
                          <ZoomOut className="w-3.5 h-3.5" />
                        </button>
                        <span className="font-mono text-[11px]">{Math.round(zoomScale * 100)}%</span>
                        <button
                          type="button"
                          onClick={() => setZoomScale((z) => Math.min(2.0, Number((z + 0.2).toFixed(1))))}
                          className="p-1 rounded hover:bg-muted"
                          title="Zoom In"
                        >
                          <ZoomIn className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Preview Canvas viewport */}
                    <div className="w-full overflow-auto py-4 flex items-center justify-center min-h-[220px] bg-neutral-100 dark:bg-neutral-900 rounded-lg border border-dashed border-border/60">
                      <div
                        style={{
                          transform: `scale(${zoomScale})`,
                          transformOrigin: "center center",
                          transition: "transform 0.15s ease",
                        }}
                      >
                        {/* The Actual Rendered Label Element (Captured for Print & Export) */}
                        <div
                          id="single-label-render-target"
                          ref={labelPreviewRef}
                          style={{
                            width: `${preset.widthPx}px`,
                            minHeight: `${preset.heightPx}px`,
                          }}
                          className={`bg-white text-black p-2.5 flex flex-col justify-between select-none shadow-md ${
                            options.showBorder ? "border border-neutral-800" : ""
                          }`}
                        >
                          {/* Top: Brand Header & Name */}
                          <div className="text-center border-b border-neutral-300 pb-1 mb-1">
                            {options.showBrand && (
                              <div className="font-bold tracking-widest text-[8px] uppercase text-neutral-800">
                                {labelData.brandName || "Lineacraft India"}
                              </div>
                            )}
                            {options.showProductName && (
                              <div className="font-bold text-[10px] leading-tight text-neutral-900 line-clamp-2 mt-0.5">
                                {labelData.productName || "Handcrafted Product"}
                              </div>
                            )}
                            {(options.showMaterial || options.showColour || options.showSize) && (
                              <div className="text-[7.5px] text-neutral-600 line-clamp-1 mt-0.5">
                                {[
                                  options.showMaterial && labelData.material,
                                  options.showColour && labelData.colour,
                                  options.showSize && labelData.size,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </div>
                            )}
                          </div>

                          {/* Middle: Scannable Crisp Barcode */}
                          <div className="my-auto py-1 text-center">
                            {options.showBarcode && (
                              <div className="w-full flex justify-center">
                                <BarcodeRenderer
                                  value={labelData.barcode || labelData.sku || "LINEACRAFT"}
                                  format={labelData.barcodeFormat}
                                  width={preset.id === "thermal_50x25" ? 1.2 : 1.4}
                                  height={preset.id === "thermal_50x25" ? 24 : 32}
                                  displayValue={false}
                                  lineColor="#000000"
                                />
                              </div>
                            )}

                            {/* SKU Code text under barcode */}
                            {options.showSku && (
                              <div className="font-mono font-bold tracking-wider text-[9px] text-neutral-900 mt-0.5">
                                {labelData.sku || labelData.barcode || "SKU-001"}
                              </div>
                            )}
                          </div>

                          {/* Bottom: Bold MRP & Compliance */}
                          <div className="border-t border-neutral-300 pt-1 mt-1">
                            {options.showMrp && (
                              <div className="flex items-baseline justify-between">
                                <div>
                                  <span className="text-[8px] font-bold uppercase tracking-wider text-neutral-800">
                                    MRP ₹{" "}
                                  </span>
                                  <span className="text-[12px] font-extrabold text-black font-price">
                                    {labelData.mrp
                                      ? Number(labelData.mrp).toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : "0.00"}
                                  </span>
                                </div>

                                {options.showOfferPrice &&
                                  labelData.salePrice &&
                                  Number(labelData.salePrice) < Number(labelData.mrp) && (
                                    <div className="text-[9px] font-bold text-neutral-900">
                                      Offer: ₹{Number(labelData.salePrice).toLocaleString("en-IN")}
                                    </div>
                                  )}
                              </div>
                            )}

                            <div className="text-[6.5px] leading-tight text-neutral-600 flex items-center justify-between mt-0.5">
                              <span>(Incl. of all taxes)</span>
                              {options.showNetQty && <span>Net Qty: {labelData.netQuantity || "1 N"}</span>}
                            </div>

                            {(options.showMfgDate || options.showOrigin) && (
                              <div className="text-[6.5px] leading-tight text-neutral-600 flex items-center justify-between border-t border-dotted border-neutral-300 mt-0.5 pt-0.5">
                                {options.showMfgDate && <span>Pkd: {labelData.mfgMonthYear}</span>}
                                {options.showOrigin && <span>{labelData.countryOfOrigin}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Print Quantity Control */}
                  <div className="bg-muted/40 p-3.5 rounded-lg border border-border flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-foreground">
                        Print Quantity
                      </label>
                      <p className="text-[11px] text-muted-foreground">
                        {preset.type === "roll"
                          ? `${printCopies} sticker copies on thermal roll`
                          : `${printCopies} labels (${Math.ceil(printCopies / (preset.perSheet || 24))} A4 sheets)`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center border border-border rounded-lg bg-background">
                        <button
                          type="button"
                          onClick={() => setPrintCopies((c) => Math.max(1, c - 1))}
                          className="px-2.5 py-1 text-sm font-bold hover:bg-muted text-muted-foreground"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          max="999"
                          data-testid="print-copies-input"
                          value={printCopies}
                          onChange={(e) => setPrintCopies(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-14 text-center font-mono font-bold text-xs py-1 outline-none bg-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => setPrintCopies((c) => c + 1)}
                          className="px-2.5 py-1 text-sm font-bold hover:bg-muted text-muted-foreground"
                        >
                          +
                        </button>
                      </div>

                      <div className="flex items-center gap-1">
                        {[1, 5, 10, preset.perSheet || 24].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setPrintCopies(n)}
                            className="px-2 py-1 text-[11px] rounded bg-muted hover:bg-muted-foreground/20 text-muted-foreground"
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Print & Export Actions */}
                <div className="pt-2 border-t border-border flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={isExporting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted text-foreground text-xs font-medium transition cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download PDF</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadPng}
                      disabled={isExporting}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted text-foreground text-xs font-medium transition cursor-pointer"
                      title="Download high-res PNG for label printer software"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      <span>PNG Image</span>
                    </button>
                  </div>

                  <button
                    type="button"
                    data-testid="print-labels-btn"
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-terracotta text-white font-medium text-xs sm:text-sm shadow-md hover:bg-terracotta/90 transition cursor-pointer"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Print {printCopies} {printCopies === 1 ? "Label" : "Labels"}</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Batch Mode: multi-SKU selection & print */
            <div className="lg:col-span-12 space-y-4">
              <div className="bg-muted/30 p-4 rounded-lg border border-border flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif font-bold text-base text-foreground flex items-center gap-2">
                    <Layers className="w-4 h-4 text-terracotta" />
                    Batch Barcode Label Generator
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Select multiple Master SKUs to generate labels in bulk. You can set individual copy counts or match your current inventory stock.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setBatchList((prev) =>
                        prev.map((item) => ({
                          ...item,
                          selected: true,
                          copies: item.stock > 0 ? item.stock : 1,
                        }))
                      );
                    }}
                    className="px-3 py-1.5 rounded bg-muted hover:bg-muted-foreground/20 text-xs text-foreground font-medium transition"
                  >
                    Select All &amp; Match Stock
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBatchList((prev) => prev.map((item) => ({ ...item, selected: false })));
                    }}
                    className="px-3 py-1.5 rounded bg-muted hover:bg-muted-foreground/20 text-xs text-foreground font-medium transition"
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {/* Search & Stats Strip */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search SKU or product in batch..."
                    value={batchSearch}
                    onChange={(e) => setBatchSearch(e.target.value)}
                    className="w-full text-xs pl-9 pr-3 py-1.5 rounded-lg bg-background border border-border outline-none focus:border-terracotta"
                  />
                </div>

                <div className="flex items-center gap-4 text-xs">
                  <span className="text-muted-foreground">
                    Selected SKUs:{" "}
                    <strong className="text-foreground">
                      {batchList.filter((b) => b.selected).length}
                    </strong>{" "}
                    / {batchList.length}
                  </span>
                  <span className="text-muted-foreground">
                    Total Labels to Print:{" "}
                    <strong className="text-terracotta font-mono font-bold text-sm">
                      {totalBatchCopies}
                    </strong>
                  </span>
                </div>
              </div>

              {/* Table of SKUs */}
              <div className="border border-border rounded-lg overflow-x-auto max-h-[46vh]">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted sticky top-0 z-10 border-b border-border">
                    <tr>
                      <th className="py-2.5 px-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={batchList.length > 0 && batchList.every((b) => b.selected)}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setBatchList((prev) => prev.map((b) => ({ ...b, selected: val })));
                          }}
                        />
                      </th>
                      <th className="py-2.5 px-3">SKU Code</th>
                      <th className="py-2.5 px-3">Product Name</th>
                      <th className="py-2.5 px-3">Category</th>
                      <th className="py-2.5 px-3 text-right">MRP (₹)</th>
                      <th className="py-2.5 px-3 text-center">In Stock</th>
                      <th className="py-2.5 px-4 text-center">Labels to Print</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredBatchList.length > 0 ? (
                      filteredBatchList.map((item) => (
                        <tr
                          key={item.id}
                          className={`hover:bg-muted/40 transition ${
                            item.selected ? "bg-terracotta/5" : ""
                          }`}
                        >
                          <td className="py-2.5 px-3 text-center">
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={(e) => {
                                const val = e.target.checked;
                                setBatchList((prev) =>
                                  prev.map((b) => (b.id === item.id ? { ...b, selected: val } : b))
                                );
                              }}
                            />
                          </td>
                          <td className="py-2.5 px-3 font-mono font-bold text-foreground">
                            {item.sku}
                          </td>
                          <td className="py-2.5 px-3 max-w-xs truncate font-medium text-foreground">
                            {item.name}
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground">
                            {item.category || "General"}
                          </td>
                          <td className="py-2.5 px-3 text-right font-price font-semibold text-foreground">
                            ₹{item.mrp || item.salePrice || "0"}
                          </td>
                          <td className="py-2.5 px-3 text-center font-mono">
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] ${
                                item.stock > 0
                                  ? "bg-muted text-foreground"
                                  : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                              }`}
                            >
                              {item.stock} pcs
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <input
                                type="number"
                                min="1"
                                max="999"
                                value={item.copies}
                                disabled={!item.selected}
                                onChange={(e) => {
                                  const c = Math.max(1, parseInt(e.target.value) || 1);
                                  setBatchList((prev) =>
                                    prev.map((b) => (b.id === item.id ? { ...b, copies: c } : b))
                                  );
                                }}
                                className="w-16 px-2 py-1 text-center font-mono font-bold text-xs bg-background border border-border rounded outline-none focus:border-terracotta disabled:opacity-40"
                              />
                              <button
                                type="button"
                                disabled={!item.selected || item.stock <= 0}
                                onClick={() => {
                                  setBatchList((prev) =>
                                    prev.map((b) =>
                                      b.id === item.id ? { ...b, copies: Math.max(1, item.stock) } : b
                                    )
                                  );
                                }}
                                className="text-[10px] text-terracotta hover:underline disabled:opacity-40 whitespace-nowrap"
                                title="Set copies equal to inventory stock"
                              >
                                = Stock
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-muted-foreground">
                          No matching SKUs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Batch Action Bar */}
              <div className="pt-3 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Target Size:</span>
                  <select
                    value={selectedPresetId}
                    onChange={(e) => setSelectedPresetId(e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded bg-background border border-border font-medium"
                  >
                    {LABEL_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={totalBatchCopies === 0 || isExporting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted text-foreground text-xs font-medium transition cursor-pointer disabled:opacity-50"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download Batch PDF</span>
                  </button>

                  <button
                    type="button"
                    data-testid="print-batch-labels-btn"
                    onClick={handlePrint}
                    disabled={totalBatchCopies === 0}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-terracotta text-white font-medium text-xs sm:text-sm shadow-md hover:bg-terracotta/90 transition cursor-pointer disabled:opacity-50"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Print {totalBatchCopies} Batch Labels</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Hidden Render Targets for Printing & PDF Generation (Clean Black & White) */}
      {/* ========================================================================= */}
      <div id="print-root-labels" className="hidden">
        {mode === "single" ? (
          // Single mode printable labels repeated printCopies times
          Array.from({ length: printCopies }).map((_, index) => (
            <div
              key={index}
              className="print-label-item"
              style={{
                width: `${preset.widthMm}mm`,
                height: `${preset.heightMm}mm`,
                pageBreakAfter: preset.type === "roll" ? "always" : "avoid",
                padding: "2mm",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily: "system-ui, -apple-system, sans-serif",
                border: options.showBorder ? "0.3mm solid #000000" : "none",
              }}
            >
              {/* Header */}
              <div style={{ textAlign: "center", borderBottom: "0.2mm solid #cccccc", paddingBottom: "1mm" }}>
                {options.showBrand && (
                  <div style={{ fontSize: "7pt", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
                    {labelData.brandName || "Lineacraft India"}
                  </div>
                )}
                {options.showProductName && (
                  <div style={{ fontSize: "8pt", fontWeight: "bold", lineHeight: 1.1, marginTop: "0.5mm" }}>
                    {labelData.productName || "Handcrafted Product"}
                  </div>
                )}
                {(options.showMaterial || options.showColour || options.showSize) && (
                  <div style={{ fontSize: "6.5pt", color: "#444444", marginTop: "0.5mm" }}>
                    {[
                      options.showMaterial && labelData.material,
                      options.showColour && labelData.colour,
                      options.showSize && labelData.size,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                )}
              </div>

              {/* Barcode */}
              <div style={{ textAlign: "center", margin: "auto 0", padding: "1mm 0" }}>
                {options.showBarcode && (
                  <BarcodeRenderer
                    value={labelData.barcode || labelData.sku || "SKU-001"}
                    format={labelData.barcodeFormat}
                    width={preset.id === "thermal_50x25" ? 1.2 : 1.4}
                    height={preset.id === "thermal_50x25" ? 22 : 30}
                    displayValue={false}
                    lineColor="#000000"
                  />
                )}
                {options.showSku && (
                  <div style={{ fontSize: "7.5pt", fontFamily: "monospace", fontWeight: "bold", marginTop: "0.5mm" }}>
                    {labelData.sku || labelData.barcode}
                  </div>
                )}
              </div>

              {/* MRP & Compliance */}
              <div style={{ borderTop: "0.2mm solid #cccccc", paddingTop: "1mm" }}>
                {options.showMrp && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div>
                      <span style={{ fontSize: "7pt", fontWeight: "bold" }}>MRP ₹ </span>
                      <span style={{ fontSize: "10pt", fontWeight: "900", fontFamily: "monospace" }}>
                        {labelData.mrp
                          ? Number(labelData.mrp).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : "0.00"}
                      </span>
                    </div>
                    {options.showOfferPrice && labelData.salePrice && (
                      <span style={{ fontSize: "8pt", fontWeight: "bold" }}>
                        Offer: ₹{Number(labelData.salePrice).toLocaleString("en-IN")}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ fontSize: "5.5pt", color: "#444444", display: "flex", justifyContent: "space-between" }}>
                  <span>(Incl. of all taxes)</span>
                  {options.showNetQty && <span>Net Qty: {labelData.netQuantity || "1 N"}</span>}
                </div>
                {(options.showMfgDate || options.showOrigin) && (
                  <div style={{ fontSize: "5.5pt", color: "#444444", display: "flex", justifyContent: "space-between", marginTop: "0.3mm" }}>
                    {options.showMfgDate && <span>Pkd: {labelData.mfgMonthYear}</span>}
                    {options.showOrigin && <span>{labelData.countryOfOrigin}</span>}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          // Batch Mode hidden printable container
          <div id="batch-render-container">
            {batchList
              .filter((b) => b.selected)
              .map((item) =>
                Array.from({ length: Number(item.copies || 1) }).map((_, cIdx) => (
                  <div
                    key={`${item.id}-${cIdx}`}
                    className="print-label-item batch-label-card"
                    data-copies={1}
                    style={{
                      width: `${preset.widthMm}mm`,
                      height: `${preset.heightMm}mm`,
                      pageBreakAfter: preset.type === "roll" ? "always" : "avoid",
                      padding: "2mm",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                      border: options.showBorder ? "0.3mm solid #000000" : "none",
                    }}
                  >
                    <div style={{ textAlign: "center", borderBottom: "0.2mm solid #cccccc", paddingBottom: "1mm" }}>
                      <div style={{ fontSize: "7pt", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
                        {labelData.brandName || "Lineacraft India"}
                      </div>
                      <div style={{ fontSize: "8pt", fontWeight: "bold", lineHeight: 1.1, marginTop: "0.5mm" }}>
                        {item.name}
                      </div>
                      {(item.material || item.colour || item.size) && (
                        <div style={{ fontSize: "6.5pt", color: "#444444", marginTop: "0.5mm" }}>
                          {[item.material, item.colour, item.size].filter(Boolean).join(" • ")}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "center", margin: "auto 0", padding: "1mm 0" }}>
                      <BarcodeRenderer
                        value={item.barcode || item.sku}
                        format="CODE128"
                        width={preset.id === "thermal_50x25" ? 1.2 : 1.4}
                        height={preset.id === "thermal_50x25" ? 22 : 30}
                        displayValue={false}
                        lineColor="#000000"
                      />
                      <div style={{ fontSize: "7.5pt", fontFamily: "monospace", fontWeight: "bold", marginTop: "0.5mm" }}>
                        {item.sku}
                      </div>
                    </div>

                    <div style={{ borderTop: "0.2mm solid #cccccc", paddingTop: "1mm" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <div>
                          <span style={{ fontSize: "7pt", fontWeight: "bold" }}>MRP ₹ </span>
                          <span style={{ fontSize: "10pt", fontWeight: "900", fontFamily: "monospace" }}>
                            {Number(item.mrp || item.salePrice || 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: "5.5pt", color: "#444444", display: "flex", justifyContent: "space-between" }}>
                        <span>(Incl. of all taxes)</span>
                        <span>Net Qty: 1 Unit</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
          </div>
        )}
      </div>

      {/* Print CSS Injection */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #print-root-labels, #print-root-labels * {
            visibility: visible !important;
          }
          #print-root-labels {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }
          .print-label-item {
            display: flex !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: ${preset.type === "roll" ? `${preset.widthMm}mm ${preset.heightMm}mm` : "A4 portrait"};
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}
